import { useState, useEffect, useMemo } from 'react';
import { useCart } from '../contexts/CartContext.tsx';
import {
  defaultSelection,
  validateSelection,
  estimateLine,
  buildLine,
} from '../lib/cart.ts';
import { formatMoney, formatPriceDelta } from '../lib/format.ts';
import type { MenuItem } from '../lib/types.ts';

interface ItemSheetProps {
  item: MenuItem;
  currency: 'RUB' | 'KZT';
  accentColor: string;
  onClose: () => void;
}

export function ItemSheet({ item, currency, accentColor, onClose }: ItemSheetProps) {
  const { addLine } = useCart();
  const [selection, setSelection] = useState<Record<string, string[]>>(() =>
    defaultSelection(item.modifier_groups),
  );
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  // Lock body scroll while sheet is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const validation = useMemo(
    () => validateSelection(item.modifier_groups, selection),
    [item.modifier_groups, selection],
  );

  const estimate = useMemo(() => estimateLine(item, selection), [item, selection]);
  const lineTotal = estimate.unitPrice * quantity;

  function toggleOption(groupLocalId: string, optionLocalId: string, isSingle: boolean, maxSelect: number) {
    setSelection((prev) => {
      const current = prev[groupLocalId] ?? [];

      if (isSingle) {
        return { ...prev, [groupLocalId]: [optionLocalId] };
      }

      if (current.includes(optionLocalId)) {
        return { ...prev, [groupLocalId]: current.filter((id) => id !== optionLocalId) };
      }

      if (current.length >= maxSelect) return prev;
      return { ...prev, [groupLocalId]: [...current, optionLocalId] };
    });
  }

  function handleAdd() {
    if (!validation.valid) {
      setShowErrors(true);
      const firstMissing = validation.missing[0];
      document.getElementById(`group-${firstMissing.local_id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    addLine(buildLine(item, selection, quantity, note));
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="item-sheet-title"
    >
      {/* Backdrop */}
      <button
        aria-label="Закрыть"
        onClick={onClose}
        className="absolute inset-0 animate-in fade-in"
        style={{ background: 'oklch(0% 0 0 / 0.5)' }}
      />

      {/* Sheet */}
      <div
        className="relative flex max-h-[92dvh] w-full flex-col overflow-hidden sm:max-w-md sm:rounded-2xl"
        style={{
          background: 'var(--surface)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2.5 pb-1 sm:hidden">
          <div
            className="h-1 w-10 rounded-full"
            style={{ background: 'var(--border-strong)' }}
          />
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {/* Image */}
          {item.image_path && (
            <div className="relative w-full" style={{ paddingBottom: '56%' }}>
              <img
                src={`/tenants/${item.image_path}`}
                alt={item.name}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          )}

          <div className="p-4">
            <h2
              id="item-sheet-title"
              className="text-xl font-semibold leading-tight"
              style={{ color: 'var(--text)' }}
            >
              {item.name}
            </h2>
            {item.description && (
              <p className="mt-1.5 text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                {item.description}
              </p>
            )}

            {/* Allergens */}
            {item.allergens.length > 0 && (
              <p className="mt-2 text-xs" style={{ color: 'var(--text-subtle)' }}>
                Аллергены: {item.allergens.join(', ')}
              </p>
            )}

            {/* Modifier groups */}
            {item.modifier_groups.map((group) => {
              const chosen = selection[group.local_id] ?? [];
              const isSingle = group.group_type === 'single';
              const isMissing = showErrors && validation.missing.some((g) => g.local_id === group.local_id);

              return (
                <fieldset
                  key={group.local_id}
                  id={`group-${group.local_id}`}
                  className="mt-5"
                >
                  <legend className="flex w-full items-baseline justify-between gap-2 pb-2">
                    <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                      {group.name}
                      {group.required && (
                        <span style={{ color: 'var(--danger)' }} aria-label="обязательно">
                          {' '}
                          *
                        </span>
                      )}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-subtle)' }}>
                      {isSingle
                        ? 'выберите одно'
                        : group.max_select > 1
                          ? `до ${group.max_select}`
                          : ''}
                    </span>
                  </legend>

                  {isMissing && (
                    <p
                      role="alert"
                      className="pb-2 text-xs font-medium"
                      style={{ color: 'var(--danger)' }}
                    >
                      Нужно выбрать{group.min_select > 1 ? ` минимум ${group.min_select}` : ''}
                    </p>
                  )}

                  <div className="flex flex-col gap-1.5">
                    {group.options.map((option) => {
                      const isChosen = chosen.includes(option.local_id);
                      const atLimit = !isSingle && !isChosen && chosen.length >= group.max_select;

                      return (
                        <label
                          key={option.local_id}
                          className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors"
                          style={{
                            background: isChosen ? 'var(--accent-soft)' : 'var(--n-50)',
                            border: `1px solid ${isChosen ? accentColor : 'var(--border)'}`,
                            opacity: atLimit ? 0.45 : 1,
                            minHeight: 44,
                          }}
                        >
                          <input
                            type={isSingle ? 'radio' : 'checkbox'}
                            name={group.local_id}
                            checked={isChosen}
                            disabled={atLimit}
                            onChange={() =>
                              toggleOption(group.local_id, option.local_id, isSingle, group.max_select)
                            }
                            className="h-4 w-4 flex-shrink-0"
                            style={{ accentColor }}
                          />
                          <span
                            className="flex-1 text-sm"
                            style={{ color: 'var(--text)' }}
                          >
                            {option.name}
                          </span>
                          {option.price_delta !== 0 && (
                            <span
                              className="flex-shrink-0 text-sm font-medium tabular-nums"
                              style={{ color: 'var(--text-muted)' }}
                            >
                              {formatPriceDelta(option.price_delta)}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              );
            })}

            {/* Note */}
            <div className="mt-5">
              <label
                htmlFor="item-note"
                className="block pb-2 text-sm font-semibold"
                style={{ color: 'var(--text)' }}
              >
                Комментарий
              </label>
              <textarea
                id="item-note"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 200))}
                rows={2}
                placeholder="Без лука, порезать пополам…"
                className="w-full resize-none rounded-lg px-3 py-2 text-sm"
                style={{
                  background: 'var(--n-50)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              />
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div
          className="flex items-center gap-3 border-t p-3"
          style={{
            background: 'var(--surface)',
            borderColor: 'var(--border)',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          }}
        >
          {/* Quantity stepper */}
          <div
            className="flex items-center rounded-lg"
            style={{ border: '1px solid var(--border)' }}
          >
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              aria-label="Уменьшить количество"
              className="flex h-11 w-11 items-center justify-center text-lg font-medium disabled:opacity-30"
              style={{ color: 'var(--text)' }}
            >
              −
            </button>
            <span
              className="w-8 text-center text-base font-semibold tabular-nums"
              style={{ color: 'var(--text)' }}
              aria-live="polite"
            >
              {quantity}
            </span>
            <button
              onClick={() => setQuantity((q) => Math.min(50, q + 1))}
              disabled={quantity >= 50}
              aria-label="Увеличить количество"
              className="flex h-11 w-11 items-center justify-center text-lg font-medium disabled:opacity-30"
              style={{ color: 'var(--text)' }}
            >
              +
            </button>
          </div>

          <button
            onClick={handleAdd}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg text-base font-semibold"
            style={{ background: accentColor, color: '#fff' }}
          >
            <span>В корзину</span>
            <span className="tabular-nums">{formatMoney(lineTotal, currency)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
